/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { listAlerts, createAlert, deleteAlert, updateAlert } from '../../api/alertsApi';
import { listWatchlistItems } from '../../api/api';
import AlertsWidget from '../../components/alerts/AlertsWidget';
import AccountSelection from '../../components/AccountSelection';
import PageBackButton from '../../components/PageBackButton';
import ConfirmModal from '../../components/ui/ConfirmModal';
import { loadWatchlistOptions } from '../../utils/watchlistOptions';
import { toast } from 'sonner';
import {
  Plus,
  Bell,
  BellOff,
  Clock,
  Repeat,
  Zap,
  Trash2,
  ExternalLink,
  Loader2,
  AlertTriangle,
  Search,
} from 'lucide-react';

/* ── helpers ──────────────────────────────────────────────── */

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function triggerLabel(mode) {
  if (mode === 'ONCE') return 'Once';
  return 'Recurring';
}

/* ── component ────────────────────────────────────────────── */

export default function AlertsListPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState();
  const [showCreate, setShowCreate] = useState(false);
  const [watchlist, setWatchlist] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [search, setSearch] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError(undefined);
    try {
      const data = await listAlerts();
      setItems(Array.isArray(data) ? data : (data?.results || []));
    } catch (e) {
      setError(e?.message || 'Failed to load alerts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  // Fetch account-specific watchlist options for symbol selection
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!selectedAccountId) {
        setWatchlist([]);
        return;
      }
      try {
        const wl = await loadWatchlistOptions(listWatchlistItems, { account_id: selectedAccountId });
        if (mounted) setWatchlist(wl);
      } catch {
        if (mounted) setWatchlist([]);
      }
    })();
    return () => { mounted = false; };
  }, [selectedAccountId]);

  const toggleActive = async (item) => {
    const prev = item.is_active;
    // Optimistic
    setItems((arr) => arr.map((x) => x.id === item.id ? { ...x, is_active: !prev } : x));
    try {
      const updated = await updateAlert(item.id, { is_active: !prev });
      setItems((arr) => arr.map((x) => x.id === item.id ? updated : x));
      toast.success(!prev ? 'Alert activated' : 'Alert paused');
    } catch (err) {
      setItems((arr) => arr.map((x) => x.id === item.id ? { ...x, is_active: prev } : x));
      toast.error(err?.message || 'Failed to update');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    try {
      await deleteAlert(deleteTarget.id);
      setItems((arr) => arr.filter((x) => x.id !== deleteTarget.id));
      toast.success('Alert deleted');
    } catch (err) {
      toast.error(err?.message || 'Delete failed');
    } finally {
      setDeleteBusy(false);
      setDeleteTarget(null);
    }
  };

  const filtered = search.trim()
    ? items.filter((it) => {
        const q = search.toLowerCase();
        return (it.name || '').toLowerCase().includes(q) || (it.symbol || '').toLowerCase().includes(q);
      })
    : items;

  const activeCount = items.filter((i) => i.is_active).length;

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-5">
      <PageBackButton label="Back to Research" to="/research" preferHistory={false} hideLabelOnMobile />

      {/* ── Header ──────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-textPrimary">Market Alerts</h1>
          {!loading && items.length > 0 && (
            <p className="text-xs text-textSecondary mt-0.5">
              {activeCount} active &middot; {items.length} total
            </p>
          )}
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg
                     bg-primary text-white hover:bg-primary/90 transition shadow-sm"
        >
          <Plus className="h-4 w-4" />
          Create Alert
        </button>
      </div>

      {/* ── Search (only when there are items) ──────────────── */}
      {!loading && items.length > 0 && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-textSecondary" />
          <input
            type="text"
            placeholder="Search by name or symbol…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 bg-backgroundSurface border border-border rounded-lg
                       text-sm text-textPrimary placeholder:text-textSecondary/60 focus:outline-none
                       focus:ring-1 focus:ring-primary/50"
          />
        </div>
      )}

      {/* ── Loading ─────────────────────────────────────────── */}
      {loading && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      )}

      {/* ── Error ───────────────────────────────────────────── */}
      {error && (
        <div className="flex items-center gap-2 rounded-lg bg-red-500/10 border border-red-500/20 p-4 text-sm text-red-400">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          {error}
        </div>
      )}

      {/* ── Empty state ─────────────────────────────────────── */}
      {!loading && !error && items.length === 0 && (
        <div className="flex flex-col items-center justify-center py-20 text-center space-y-3">
          <div className="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center">
            <Bell className="h-7 w-7 text-primary" />
          </div>
          <div>
            <p className="text-base font-medium text-textPrimary">No market alerts yet</p>
            <p className="text-sm text-textSecondary mt-1">
              Create your first alert to get notified when market conditions match your criteria.
            </p>
          </div>
          <button
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg
                       bg-primary text-white hover:bg-primary/90 transition shadow-sm mt-2"
          >
            <Plus className="h-4 w-4" />
            Create Alert
          </button>
        </div>
      )}

      {/* ── No search results ───────────────────────────────── */}
      {!loading && !error && items.length > 0 && filtered.length === 0 && (
        <div className="text-center py-12 text-textSecondary text-sm">
          No alerts match &ldquo;{search}&rdquo;
        </div>
      )}

      {/* ── Alert cards ─────────────────────────────────────── */}
      {!loading && !error && filtered.length > 0 && (
        <div className="grid gap-3">
          {filtered.map((item) => (
            <div
              key={item.id}
              className="bg-backgroundSurface border border-border rounded-lg p-4
                         hover:border-primary/40 transition group"
            >
              <div className="flex items-start justify-between gap-3">
                {/* Left */}
                <div
                  className="flex items-center gap-3 min-w-0 cursor-pointer flex-1"
                  onClick={() => navigate(`/alerts/${item.id}`)}
                >
                  <div className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${
                    item.is_active ? 'bg-primary/10' : 'bg-gray-500/10'
                  }`}>
                    {item.is_active
                      ? <Bell className="h-4.5 w-4.5 text-primary" />
                      : <BellOff className="h-4.5 w-4.5 text-gray-500" />
                    }
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-textPrimary truncate">{item.name}</span>
                      <span className={`px-2 py-0.5 text-xs rounded-full ${
                        item.is_active
                          ? 'bg-green-500/15 text-green-400'
                          : 'bg-gray-500/15 text-gray-400'
                      }`}>
                        {item.is_active ? 'Active' : 'Paused'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5 text-xs text-textSecondary flex-wrap">
                      <span className="font-medium text-textPrimary/80">{item.symbol}</span>
                      <span>&middot;</span>
                      <span>{item.timeframe}</span>
                      <span>&middot;</span>
                      <span className="inline-flex items-center gap-0.5">
                        {item.trigger_mode === 'ONCE' ? <Zap className="h-3 w-3" /> : <Repeat className="h-3 w-3" />}
                        {triggerLabel(item.trigger_mode)}
                      </span>
                      {item.triggered_count > 0 && (
                        <>
                          <span>&middot;</span>
                          <span className="inline-flex items-center gap-0.5">
                            <Clock className="h-3 w-3" />
                            {item.triggered_count}x triggered
                          </span>
                        </>
                      )}
                      {item.last_triggered_at && (
                        <>
                          <span>&middot;</span>
                          <span>Last: {timeAgo(item.last_triggered_at)}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Right actions */}
                <div className="flex items-center gap-2 shrink-0">
                  {/* Toggle switch */}
                  <button
                    onClick={() => toggleActive(item)}
                    className={`relative w-9 h-5 rounded-full transition-colors ${
                      item.is_active ? 'bg-primary' : 'bg-gray-600'
                    }`}
                    title={item.is_active ? 'Pause alert' : 'Activate alert'}
                  >
                    <span className={`absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white transition-transform ${
                      item.is_active ? 'translate-x-4' : 'translate-x-0'
                    }`} />
                  </button>

                  {/* Open */}
                  <button
                    onClick={() => navigate(`/alerts/${item.id}`)}
                    className="p-1.5 rounded-lg text-textSecondary hover:text-primary hover:bg-primary/10 transition"
                    title="Open alert"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </button>

                  {/* Delete */}
                  <button
                    onClick={() => setDeleteTarget(item)}
                    className="p-1.5 rounded-lg text-textSecondary hover:text-red-400 hover:bg-red-500/10 transition"
                    title="Delete alert"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Delete confirmation ─────────────────────────────── */}
      <ConfirmModal
        open={!!deleteTarget}
        title="Delete Alert"
        message={
          <span>
            Delete <strong>{deleteTarget?.name}</strong>? This cannot be undone.
          </span>
        }
        confirmLabel="Delete"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
        busy={deleteBusy}
      />

      {/* ── Create modal ────────────────────────────────────── */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 flex items-start justify-center p-2 sm:p-0 overflow-y-auto">
          <div className="mt-4 sm:mt-10 w-full max-w-[95vw] sm:max-w-[900px] bg-backgroundSurface border border-border rounded-xl shadow-2xl p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base sm:text-lg font-bold text-textPrimary">Create Alert</h2>
              <button
                className="p-1.5 rounded-lg text-textSecondary hover:text-textPrimary hover:bg-muted transition"
                onClick={() => setShowCreate(false)}
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            {/* Account selector */}
            <div className="mb-4">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                <AccountSelection
                  selectedAccount={selectedAccountId}
                  setSelectedAccount={setSelectedAccountId}
                  displayMode="compact"
                />
                <div className="text-xs text-textSecondary">Select an account to validate symbols.</div>
              </div>
            </div>
            <AlertsWidget
              watchlist={watchlist}
              accountId={selectedAccountId}
              onBuild={async (payload) => {
                try {
                  const created = await createAlert(payload);
                  setShowCreate(false);
                  toast.success('Alert created');
                  setItems((arr) => [created, ...arr]);
                } catch (err) {
                  toast.error(err?.message || 'Failed to create alert');
                }
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
